//changed due to package upgrade
import { useEffect, useState } from 'react';
import AppGridContainer from '@projectK/components/AppGridContainer';
import AppTextField from '@projectK/components/AppTextField';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import EditIcon from '@mui/icons-material/Edit';
import AppCard from '@projectK/components/AppCard';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import AppInfoView from '@projectK/components/AppInfoView';
import IntlMessages from '@projectK/helpers/IntlMessages';
import * as yup from 'yup';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PropTypes from 'prop-types';
import {
  useRolePermissions,
  hasPermission,
} from '@projectK/context/RolesProvider';

const validationSchema = yup.object({
  hostname: yup
    .string()
    .required(<IntlMessages id='validation.hostname.required' />),
  port: yup
    .number()
    .typeError(<IntlMessages id='validation.port.type' />)
    .integer(<IntlMessages id='validation.port.integer' />)
    .positive(<IntlMessages id='validation.port.positive' />)
    .test(
      'is-valid-port',
      () => <IntlMessages id='validation.port.invalid' />,
      (value) => {
        if (!value) return true; // allow empty value
        return /^\d{3,4}$/.test(String(value));
      },
    )
    .required(<IntlMessages id='validation.port.required' />),
  email_from: yup
    .string()
    .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, () => (
      <IntlMessages id='validation.invalidEmailFormat' />
    ))
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
  username: yup
    .string()
    .required(<IntlMessages id='validation.nameRequired' />),
  password: yup
    .string()
    .required(<IntlMessages id='validation.passwordRequired' />),
});

const EmailConfigSettings = ({ emailSettings }) => {
  const companyTheme = CompanyTheme();
  const [isEdit, setIsEdit] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [emailSignatures, setEmailSignatures] = useState([]);
  const { rolePermissions } = useRolePermissions();

  const onShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const onDownNewPassword = (event) => {
    event.preventDefault();
  };
  const fetchEmailSignatures = async () => {
    try {
      const response = await jwtAxios.get('/email-signature', {
        params: {
          perPage: 'all',
          filters: {
            is_active: 1,
          },
        },
      });
      const { data } = response;
      setEmailSignatures(data.list);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchEmailSignatures();
  }, []);
  return (
    <>
      <AppCard>
        <Formik
          validateOnChange={true}
          initialValues={{
            hostname: emailSettings?.hostname || '',
            port: emailSettings?.port || '',
            email_from: emailSettings?.email_from || '',
            username: emailSettings?.username || '',
            password: emailSettings?.password || '',
            email_signature: emailSettings?.email_signature || null,
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            jwtAxios
              .post(`/emailconfig`, values)
              .then((response) => {
                setSubmitting(false);
                setIsEdit(false);
                infoViewActionsContext.showMessage(
                  'Email Config Settings updated successfully!',
                );
              })
              .catch((error) => {
                infoViewActionsContext.fetchError(error.response.data.error);
                setSubmitting(false);
              });
          }}
        >
          {({ resetForm, values, setFieldValue }) => (
            <Form noValidate autoComplete='off'>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant='h4'
                    sx={{
                      color: companyTheme
                        ? companyTheme.primary.main
                        : 'text.primary',
                    }}
                  >
                    Email Config Settings
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ mt: 2, color: 'text.secondary' }}
                  >
                    Manages configuration options for email-related parameters
                    and preferences
                  </Typography>
                </Box>
                {hasPermission(
                  rolePermissions,
                  'Communication - Email Config',
                  'create',
                ) && (
                  <Box>
                    {!isEdit && (
                      <EditIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setIsEdit(true)}
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 4 }} />
              <AppGridContainer>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Box
                    sx={{
                      border: '1px solid #EAECF0',
                      p: 6,
                      borderRadius: 3,
                      boxShadow:
                        '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                    }}
                  >
                    <AppGridContainer>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='hostname'
                          values={emailSettings?.hostname}
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='Host Name'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='port'
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='Port Number'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='email_from'
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='Email From'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='username'
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='User Name'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='password'
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton
                                  aria-label='toggle password visibility'
                                  onClick={onShowNewPassword}
                                  onMouseDown={onDownNewPassword}
                                  edge='end'
                                  disabled={!isEdit}
                                >
                                  {showNewPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                          type={showNewPassword ? 'text' : 'password'}
                          variant='outlined'
                          sx={{
                            width: '100%',
                          }}
                          label='Password'
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                          options={emailSignatures}
                          getOptionLabel={(option) =>
                            `${option.name} - ${option.signature}`
                          }
                          getOptionKey={(option) => option.id}
                          name='email_signature'
                          onChange={(_, value) => {
                            setFieldValue('email_signature', value?.id);
                          }}
                          value={
                            emailSignatures.find(
                              (signature) =>
                                signature.id === values.email_signature,
                            ) || null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Signature'
                              variant='outlined'
                            />
                          )}
                          readOnly={!isEdit}
                        />
                      </Grid>
                    </AppGridContainer>
                  </Box>
                </Grid>
              </AppGridContainer>
              {isEdit && (
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  spacing={5}
                  sx={{ mt: 3 }}
                >
                  <Button
                    sx={{
                      borderColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        borderColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8)
                          : alpha('#2196f3', 0.8),
                      },
                      color: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                    }}
                    onClick={() => {
                      setIsEdit(false);
                      resetForm();
                      setShowNewPassword(false);
                      setFieldValue(values);
                    }}
                  >
                    <IntlMessages id='common.cancel' />
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    sx={{
                      backgroundColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        backgroundColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                          : alpha('#2196f3', 0.8),
                      },
                    }}
                    onClick={() => {
                      setShowNewPassword(false);
                    }}
                  >
                    <IntlMessages id='common.save' />
                  </Button>
                </Stack>
              )}
            </Form>
          )}
        </Formik>
      </AppCard>
      <AppInfoView />
    </>
  );
};
export default EmailConfigSettings;
EmailConfigSettings.propTypes = {
  emailSettings: PropTypes.object,
};
